import { useState, useEffect, useMemo } from 'react';
import { Mail, ArrowRight, KeyRound } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import { ReachLogo } from '../components/Logo';
import { TurnstileWidget } from '../components/TurnstileWidget';

type AuthMode = 'password' | 'magic';

export function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [mode, setMode] = useState<AuthMode>('magic');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileResetSignal, setTurnstileResetSignal] = useState(0);
  const domain = useMemo(() => new URLSearchParams(window.location.search).get('domain') ?? undefined, []);
  const wpState = useMemo(() => new URLSearchParams(window.location.search).get('wp_state') ?? undefined, []);
  const wpCallbackUrl = useMemo(() => new URLSearchParams(window.location.search).get('wp_callback') ?? undefined, []);
  const isWordPressConnect = Boolean(domain && wpState && wpCallbackUrl);
  const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY || '';
  const requiresTurnstile = Boolean(turnstileSiteKey);

  useEffect(() => {
    if (countdown <= 0) return;
    const t = setInterval(() => setCountdown((n) => n - 1), 1000);
    return () => clearInterval(t);
  }, [countdown]);

  const resetTurnstile = () => {
    setTurnstileToken('');
    setTurnstileResetSignal((value) => value + 1);
  };

  useEffect(() => {
    setError('');
    setSent(false);
    resetTurnstile();
  }, [mode]);

  const handleMagicLinkSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (countdown > 0) return;
    setLoading(true);
    setError('');
    try {
      await apiFetch('/api/auth/request-magic-link', {
        method: 'POST',
        body: JSON.stringify({
          email,
          domain,
          turnstileToken,
          wpState,
          wpCallbackUrl,
        }),
      });
      setSent(true);
      setCountdown(60);
      resetTurnstile();
    } catch (err: any) {
      setError(err.message || 'Failed to send magic link');
      resetTurnstile();
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await apiFetch('/api/auth/console/admin-login', {
        method: 'POST',
        body: JSON.stringify({ email, password, turnstileToken }),
      });
      navigate('/', { replace: true });
    } catch (err: any) {
      setError(err.message || 'Failed to sign in');
      resetTurnstile();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex w-[420px] flex-shrink-0 flex-col items-center justify-center gap-8 p-12 relative overflow-hidden" style={{ background: 'var(--console-workspace-header-start)' }}>
        <div className="flex items-center gap-5">
          <div style={{ background: 'rgba(255,255,255,0.12)', borderRadius: '12px', padding: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <ReachLogo size="large" showText={false} color="rgba(255,255,255,0.95)" detailColor="#0891b2" />
          </div>
          <span style={{ fontSize: '28px', fontWeight: 800, color: '#fff', letterSpacing: '0.06em', fontFamily: "'Inter', sans-serif", lineHeight: 1 }}>
            REACH<span style={{ color: '#22d3ee', fontSize: '34px', lineHeight: 0 }}>.</span>
          </span>
        </div>
        <p className="text-zinc-200 text-sm text-center max-w-xs leading-relaxed">
          The intelligent connectivity layer for modern commerce.
        </p>
        <div className="absolute bottom-8 text-xs text-zinc-700">console.veahub.com</div>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center bg-white px-6 py-12">
        <div className="lg:hidden mb-10">
          <div className="flex items-center gap-3">
            <div style={{ background: 'rgba(15,15,16,0.08)', borderRadius: '12px', padding: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ReachLogo size="medium" showText={false} color="#0b2940" detailColor="#0891b2" />
            </div>
            <span style={{ fontSize: '22px', fontWeight: 800, color: '#0f0f10', letterSpacing: '0.06em', fontFamily: "'Inter', sans-serif", lineHeight: 1 }}>
              REACH<span style={{ color: '#0891b2', fontSize: '28px', lineHeight: 0 }}>.</span>
            </span>
          </div>
        </div>

        <div className="w-full max-w-sm">
          {mode === 'password' ? (
            <>
              <div className="mb-8">
                <h1 className="text-2xl font-bold text-zinc-900 tracking-tight">
                  {isWordPressConnect ? 'Credentials fallback' : 'Credentials sign in'}
                </h1>
                <p className="text-sm text-zinc-500 mt-1.5">
                  {isWordPressConnect
                    ? 'Use this only if the magic link flow is not available for this connection.'
                    : 'Use your account password if you prefer not to use a magic link.'}
                </p>
              </div>

              <form onSubmit={handlePasswordSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-1.5">Email address</label>
                  <input
                    type="email"
                    required
                    autoFocus
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full px-3.5 py-2.5 rounded-xl border border-zinc-200 text-sm bg-zinc-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-cyan-600 focus:border-transparent transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-1.5">Password</label>
                  <input
                    type="password"
                    required
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    className="w-full px-3.5 py-2.5 rounded-xl border border-zinc-200 text-sm bg-zinc-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-cyan-600 focus:border-transparent transition-colors"
                  />
                </div>
                <TurnstileWidget
                  siteKey={turnstileSiteKey}
                  resetSignal={turnstileResetSignal}
                  onTokenChange={setTurnstileToken}
                />
                {error && (
                  <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</p>
                )}
                <button
                  type="submit"
                  disabled={loading || !email || !password || (requiresTurnstile && !turnstileToken)}
                  className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-[#0f3d5e] text-white text-sm font-semibold hover:bg-[#124a66] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? (
                    <span className="h-4 w-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                  ) : (
                    <>Sign in <KeyRound size={15} /></>
                  )}
                </button>
              </form>
              <div className="mt-4 text-center">
                <button
                  type="button"
                  onClick={() => setMode('magic')}
                  className="text-sm text-slate-500 hover:text-cyan-700 transition-colors"
                >
                  {isWordPressConnect ? 'Back to magic link' : 'Use magic link instead'}
                </button>
              </div>
            </>
          ) : (
            <>
              {!sent ? (
                <>
                  <div className="mb-8">
                    <h1 className="text-2xl font-bold text-zinc-900 tracking-tight">
                      {isWordPressConnect ? 'Connect your WordPress store' : 'Magic link sign in'}
                    </h1>
                    <p className="text-sm text-zinc-500 mt-1.5">
                      {isWordPressConnect
                        ? 'Sign in to Reach and we’ll send you back to WordPress to finish linking this store.'
                        : 'We’ll email you a short-lived sign-in link.'}
                    </p>
                  </div>

                  <form onSubmit={handleMagicLinkSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-zinc-700 mb-1.5">Email address</label>
                      <input
                        type="email"
                        required
                        autoFocus
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        placeholder="you@example.com"
                        className="w-full px-3.5 py-2.5 rounded-xl border border-zinc-200 text-sm bg-zinc-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-cyan-600 focus:border-transparent transition-colors"
                      />
                    </div>
                    <TurnstileWidget
                      siteKey={turnstileSiteKey}
                      resetSignal={turnstileResetSignal}
                      onTokenChange={setTurnstileToken}
                    />
                    {error && (
                      <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</p>
                    )}
                    <button
                      type="submit"
                      disabled={loading || !email || (requiresTurnstile && !turnstileToken)}
                      className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-[#0f3d5e] text-white text-sm font-semibold hover:bg-[#124a66] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      {loading ? (
                        <span className="h-4 w-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                      ) : (
                        <>Send magic link <ArrowRight size={15} /></>
                      )}
                    </button>
                  </form>
                </>
              ) : (
                <div className="text-center space-y-5">
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-zinc-100">
                    <Mail size={22} className="text-zinc-700" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-zinc-900 tracking-tight">Check your inbox</h2>
                    <p className="text-sm text-zinc-500 mt-1.5">
                      We sent a sign-in link to{' '}
                      <span className="font-semibold text-zinc-800">{email}</span>
                    </p>
                  </div>
                  <p className="text-xs text-zinc-400">The link expires in 15 minutes.</p>
                  <TurnstileWidget
                    siteKey={turnstileSiteKey}
                    resetSignal={turnstileResetSignal}
                    onTokenChange={setTurnstileToken}
                  />
                  {error && (
                    <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2 text-left">{error}</p>
                  )}
                  <div className="pt-2 border-t border-zinc-100">
                    <button
                      onClick={handleMagicLinkSubmit as any}
                      disabled={countdown > 0 || loading || (requiresTurnstile && !turnstileToken)}
                      className="text-sm text-slate-500 hover:text-cyan-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      {countdown > 0 ? `Resend in ${countdown}s` : 'Resend link'}
                    </button>
                  </div>
                </div>
              )}
              <div className="mt-4 text-center">
                <button
                  type="button"
                  onClick={() => setMode('password')}
                  className="text-sm text-slate-500 hover:text-cyan-700 transition-colors"
                >
                  {isWordPressConnect ? 'Use credentials fallback' : 'Use credentials instead'}
                </button>
              </div>
            </>
          )}
        </div>

        <p className="mt-12 text-xs text-zinc-300">© {new Date().getFullYear()} VeaHub</p>
      </div>
    </div>
  );
}
